import { useCallback, useContext, useEffect } from 'react';

import {
  CLEAR_ANNOTATIONS_SELECTIONS,
  DUPLICATE_ANNOTATIONS,
  REDO,
  REMOVE_ANNOTATIONS,
  SELECT_ALL_ANNOTATIONS,
  SELECT_TAB,
  SELECT_TOOL,
  SET_ANNOTATION,
  TOGGLE_ORIGINAL_IMAGE_DISPLAY,
  UNDO,
  ZOOM_CANVAS,
} from '../actions';
import { CHANGE_POINTER_ICON } from '../actions/changePointerIcon';
import { SET_EDITOR_PREFERENCES } from '../actions/setEditorPreferences';
import { EditorContext } from '../../../../imageEditorComponent';
import {
  ANNOTATE_DRAW_TOOL_IDS,
  CANVAS_INTERACTION_MODES,
  POINTER_ICONS,
  TABS_IDS,
  TOOLS_IDS,
  WATERMARK_ANNOTATION_ID,
} from '../utils/constants';
import { isCanvasPanMode } from '../utils/canvasInteractionMode';
import { MOVE_ANNOTATIONS } from '../actions';
import { getIdsToMoveWithPrimary } from '../utils/annotationGroupUtils';
import { filterUnlockedAnnotationIds } from '../utils/annotationLockUtils';
import buildNudgeAnnotationPayload, {
  getNudgeDeltaFromArrowKey,
} from '../utils/buildNudgeAnnotationPayload';
import {
  CANVAS_ZOOM_STEP_MULTIPLIER,
  getCanvasFitZoomFactor,
  getCanvasPresetZoomFactor,
} from '../utils/canvasZoomShortcutFactors';
import shouldIgnoreEditorKeyboardShortcut, {
  isEditorModalOpen,
} from '../utils/shouldIgnoreEditorKeyboardShortcut';
import useReorderSelectedAnnotations from './useReorderSelectedAnnotations';
import useGroupLockActions from './useGroupLockActions';
import useMultiSelectionLayout from './useMultiSelectionLayout';
import useAnnotationClipboard from './useAnnotationClipboard';
import usePasteImageFromClipboard from './usePasteImageFromClipboard';
import matchEditorLayoutShortcut from '../utils/matchEditorLayoutShortcut';
import useStore from './useStore';

const ARROW_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

const isBracketRightKey = (event) =>
  event.key === ']' || event.code === 'BracketRight';

const isBracketLeftKey = (event) =>
  event.key === '[' || event.code === 'BracketLeft';

const isActiveAnnotateDrawTool = (activeToolId, activeTabId) =>
  activeTabId === TABS_IDS.ANNOTATE &&
  activeToolId &&
  ANNOTATE_DRAW_TOOL_IDS.includes(activeToolId);

const getLayerDirectionFromBracketKey = (event) => {
  if (isBracketRightKey(event)) {
    return event.shiftKey ? 'front' : 'forward';
  }
  if (isBracketLeftKey(event)) {
    return event.shiftKey ? 'back' : 'backward';
  }
  return null;
};

const useEditorKeyboardShortcuts = () => {
  const { setCurrentId, setCurrentTab } = useContext(EditorContext);
  const {
    dispatch,
    tabId,
    selectionsIds = [],
    annotations = {},
    designLayer,
    textIdOfEditableContent,
    hasUndo = false,
    hasRedo = false,
    feedback = {},
    zoom = {},
    toolId,
    shownImageDimensions,
    resize,
    originalImage,
    isResetted = true,
    isShowOriginalImage = false,
    adjustments: { crop } = {},
    config: { disableZooming = false, keyboardShortcutHandlersRef } = {},
    editorPreferences = {},
  } = useStore();

  const isBlockerError = feedback.duration === 0;
  const selectionsLength = selectionsIds.length;
  const isZoomShortcutDisabled =
    disableZooming || toolId === TOOLS_IDS.CROP || isBlockerError;
  const { reorder: reorderSelectedLayers } = useReorderSelectedAnnotations();
  const {
    group: groupSelection,
    ungroup: ungroupSelection,
    lock: lockSelection,
    unlock: unlockSelection,
    floatingLockAction,
  } = useGroupLockActions();
  const { align: alignSelection, distribute: distributeSelection } =
    useMultiSelectionLayout();
  const { pasteImageFromClipboard } = usePasteImageFromClipboard();
  const {
    copySelection,
    pasteSelection,
    cutSelection,
    hasClipboardData,
  } = useAnnotationClipboard();

  const duplicateSelectedNodes = useCallback(() => {
    const annotationsIds = filterUnlockedAnnotationIds(
      annotations,
      selectionsIds,
    );
    if (annotationsIds.length === 0) {
      return;
    }
    dispatch({
      type: DUPLICATE_ANNOTATIONS,
      payload: {
        annotationsIds,
      },
    });
  }, [annotations, dispatch, selectionsIds]);

  const removeSelectedNodes = useCallback(() => {
    const annotationsIds = filterUnlockedAnnotationIds(
      annotations,
      selectionsIds,
    );
    if (annotationsIds.length === 0) {
      return;
    }
    dispatch({
      type: REMOVE_ANNOTATIONS,
      payload: {
        annotationsIds,
      },
    });
  }, [annotations, dispatch, selectionsIds]);

  const nudgeSelectedNodes = useCallback(
    (event) => {
      const delta = getNudgeDeltaFromArrowKey(event.key, event.shiftKey);
      if (!delta) {
        return;
      }

      const idsToNudge = new Set();
      selectionsIds.forEach((id) => {
        getIdsToMoveWithPrimary(annotations, id, selectionsIds).forEach(
          (memberId) => idsToNudge.add(memberId),
        );
      });

      const updates = [...idsToNudge]
        .map((id) => {
          const annotation = annotations[id];
          if (!annotation || annotation.locked) {
            return null;
          }
          return buildNudgeAnnotationPayload(annotation, delta.dx, delta.dy);
        })
        .filter(Boolean);

      if (updates.length === 0) {
        return;
      }

      dispatch({
        type: MOVE_ANNOTATIONS,
        payload: { updates },
      });

      updates.forEach((payload) => {
        const node = designLayer?.findOne(`#${payload.id}`);
        if (!node) {
          return;
        }
        if (payload.points) {
          node.points(payload.points);
        } else {
          node.setAttrs({ x: payload.x, y: payload.y });
        }
      });
    },
    [annotations, designLayer, dispatch, selectionsIds],
  );

  const dispatchZoomFactor = useCallback(
    (factor) => {
      if (factor == null || Number.isNaN(factor)) {
        return;
      }
      dispatch({
        type: ZOOM_CANVAS,
        payload: { factor },
      });
    },
    [dispatch],
  );

  const hideOriginalImage = useCallback(() => {
    if (!isShowOriginalImage) {
      return;
    }
    dispatch({
      type: TOGGLE_ORIGINAL_IMAGE_DISPLAY,
      payload: { isShow: false },
    });
  }, [dispatch, isShowOriginalImage]);

  const showOriginalImage = useCallback(() => {
    if (isResetted || isBlockerError || !originalImage) {
      return;
    }
    dispatch({
      type: TOGGLE_ORIGINAL_IMAGE_DISPLAY,
      payload: { isShow: true },
    });
  }, [dispatch, isResetted, isBlockerError, originalImage]);

  const deselectSidebarTab = useCallback(() => {
    setCurrentTab(null);
    dispatch({
      type: SELECT_TAB,
      payload: { tabId: null },
    });
  }, [dispatch, setCurrentTab]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (shouldIgnoreEditorKeyboardShortcut(event)) {
        return;
      }

      const mod = event.metaKey || event.ctrlKey;
      const modalOpen = isEditorModalOpen();

      if (
        mod &&
        !event.shiftKey &&
        (event.key === 'z' || event.key === 'Z') &&
        hasUndo &&
        !isBlockerError
      ) {
        event.preventDefault();
        dispatch({ type: UNDO });
        return;
      }

      const isRedoKey =
        (mod &&
          event.shiftKey &&
          (event.key === 'z' || event.key === 'Z') &&
          hasRedo) ||
        (mod && (event.key === 'y' || event.key === 'Y') && hasRedo);

      if (isRedoKey) {
        event.preventDefault();
        dispatch({ type: REDO });
        return;
      }

      if (
        mod &&
        (event.key === 's' || event.key === 'S') &&
        !modalOpen &&
        !textIdOfEditableContent
      ) {
        event.preventDefault();
        keyboardShortcutHandlersRef?.current?.openSaveMenu?.();
        return;
      }

      if (
        mod &&
        (event.key === 'c' || event.key === 'C') &&
        !modalOpen &&
        !textIdOfEditableContent &&
        selectionsLength > 0
      ) {
        if (copySelection()) {
          event.preventDefault();
        }
        return;
      }

      if (
        mod &&
        (event.key === 'x' || event.key === 'X') &&
        !modalOpen &&
        !textIdOfEditableContent &&
        selectionsLength > 0
      ) {
        if (cutSelection()) {
          event.preventDefault();
        }
        return;
      }

      if (
        mod &&
        (event.key === 'v' || event.key === 'V') &&
        !modalOpen &&
        !textIdOfEditableContent &&
        tabId === TABS_IDS.ANNOTATE
      ) {
        const tryPasteCopiedLayers = () => {
          if (hasClipboardData()) {
            return pasteSelection();
          }
          return false;
        };

        if (navigator.clipboard?.read) {
          event.preventDefault();
          pasteImageFromClipboard().then((imageHandled) => {
            if (!imageHandled) {
              tryPasteCopiedLayers();
            }
          });
          return;
        }

        if (tryPasteCopiedLayers()) {
          event.preventDefault();
        }
        return;
      }

      if (!isZoomShortcutDisabled && mod) {
        if (event.key === '0') {
          event.preventDefault();
          dispatchZoomFactor(
            getCanvasFitZoomFactor({
              resize,
              crop,
              shownImageDimensions,
            }),
          );
          return;
        }

        if (event.key === '1') {
          const actualFactor = getCanvasPresetZoomFactor({
            presetFactor: 1,
            originalImage,
            shownImageDimensions,
          });
          if (actualFactor != null) {
            event.preventDefault();
            dispatchZoomFactor(actualFactor);
          }
          return;
        }

        if (event.key === '=' || event.key === '+') {
          event.preventDefault();
          dispatchZoomFactor(
            (zoom.factor || 1) * CANVAS_ZOOM_STEP_MULTIPLIER,
          );
          return;
        }

        if (event.key === '-' || event.key === '_') {
          event.preventDefault();
          dispatchZoomFactor(
            (zoom.factor || 1) / CANVAS_ZOOM_STEP_MULTIPLIER,
          );
          return;
        }
      }

      if (
        mod &&
        !event.repeat &&
        selectionsLength > 0 &&
        (isBracketRightKey(event) || isBracketLeftKey(event))
      ) {
        const direction = getLayerDirectionFromBracketKey(event);
        if (direction) {
          event.preventDefault();
          reorderSelectedLayers(direction);
        }
        return;
      }

      if (
        (event.key === 'o' || event.key === 'O') &&
        !mod &&
        !textIdOfEditableContent &&
        !modalOpen
      ) {
        if (!event.repeat) {
          event.preventDefault();
          showOriginalImage();
        }
        return;
      }

      if (event.key === 'Escape' && !textIdOfEditableContent && !modalOpen) {
        if (isCanvasPanMode(editorPreferences)) {
          event.preventDefault();
          dispatch({
            type: SET_EDITOR_PREFERENCES,
            payload: { canvasInteractionMode: CANVAS_INTERACTION_MODES.SELECT },
          });
          dispatch({
            type: CHANGE_POINTER_ICON,
            payload: { pointerCssIcon: POINTER_ICONS.DEFAULT },
          });
          return;
        }

        if (editorPreferences.layersPanelOpen) {
          event.preventDefault();
          dispatch({
            type: SET_EDITOR_PREFERENCES,
            payload: { layersPanelOpen: false },
          });
          return;
        }

        if (selectionsLength > 0) {
          event.preventDefault();
          dispatch({ type: CLEAR_ANNOTATIONS_SELECTIONS });
          return;
        }

        if (isActiveAnnotateDrawTool(toolId, tabId)) {
          event.preventDefault();
          dispatch({
            type: SELECT_TOOL,
            payload: { toolId: null, keepSelections: true },
          });
          setCurrentId('');
          return;
        }

        if (tabId != null && tabId !== '') {
          event.preventDefault();
          deselectSidebarTab();
          return;
        }
      }

      if (
        mod &&
        (event.key === 'a' || event.key === 'A') &&
        !textIdOfEditableContent &&
        !modalOpen
      ) {
        event.preventDefault();
        dispatch({ type: SELECT_ALL_ANNOTATIONS });
        return;
      }

      if (textIdOfEditableContent || modalOpen) {
        return;
      }

      if (
        !mod &&
        !event.altKey &&
        (event.key === 'v' || event.key === 'V')
      ) {
        event.preventDefault();
        dispatch({
          type: SET_EDITOR_PREFERENCES,
          payload: { canvasInteractionMode: CANVAS_INTERACTION_MODES.SELECT },
        });
        dispatch({
          type: CHANGE_POINTER_ICON,
          payload: { pointerCssIcon: POINTER_ICONS.DEFAULT },
        });
        return;
      }

      if (
        !mod &&
        !event.altKey &&
        (event.key === 'h' || event.key === 'H')
      ) {
        event.preventDefault();
        dispatch({
          type: SET_EDITOR_PREFERENCES,
          payload: { canvasInteractionMode: CANVAS_INTERACTION_MODES.PAN },
        });
        dispatch({
          type: CHANGE_POINTER_ICON,
          payload: { pointerCssIcon: POINTER_ICONS.DRAG },
        });
        return;
      }

      if (selectionsLength > 0) {
        const layoutShortcut = matchEditorLayoutShortcut(event);
        if (layoutShortcut) {
          let handled = false;

          if (layoutShortcut.type === 'group') {
            handled = groupSelection();
          } else if (layoutShortcut.type === 'ungroup') {
            handled = ungroupSelection();
          } else if (layoutShortcut.type === 'toggleLock') {
            handled =
              floatingLockAction === 'unlock'
                ? unlockSelection()
                : lockSelection();
          } else if (layoutShortcut.type === 'align') {
            handled = alignSelection(layoutShortcut.alignment);
          } else if (layoutShortcut.type === 'distribute') {
            handled = distributeSelection(layoutShortcut.direction);
          }

          if (handled) {
            event.preventDefault();
            return;
          }
        }
      }

      if (ARROW_KEYS.has(event.key) && selectionsLength > 0) {
        event.preventDefault();
        nudgeSelectedNodes(event);
        return;
      }

      const isDeleteKey = event.key === 'Delete' || event.key === 'Backspace';
      const isDuplicateKey = mod && (event.key === 'd' || event.key === 'D');

      if (selectionsLength === 0) {
        return;
      }

      if (isDeleteKey) {
        event.preventDefault();
        removeSelectedNodes();
        return;
      }

      if (isDuplicateKey && selectionsIds[0] !== WATERMARK_ANNOTATION_ID) {
        event.preventDefault();
        duplicateSelectedNodes();
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === 'o' || event.key === 'O') {
        hideOriginalImage();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    dispatch,
    tabId,
    toolId,
    setCurrentId,
    setCurrentTab,
    deselectSidebarTab,
    editorPreferences,
    hasUndo,
    hasRedo,
    isBlockerError,
    isZoomShortcutDisabled,
    textIdOfEditableContent,
    selectionsLength,
    selectionsIds,
    removeSelectedNodes,
    duplicateSelectedNodes,
    reorderSelectedLayers,
    groupSelection,
    ungroupSelection,
    lockSelection,
    unlockSelection,
    floatingLockAction,
    alignSelection,
    distributeSelection,
    nudgeSelectedNodes,
    dispatchZoomFactor,
    resize,
    crop,
    shownImageDimensions,
    originalImage,
    zoom.factor,
    keyboardShortcutHandlersRef,
    showOriginalImage,
    hideOriginalImage,
    isResetted,
    pasteImageFromClipboard,
    copySelection,
    pasteSelection,
    cutSelection,
    hasClipboardData,
  ]);

  return null;
};

export default useEditorKeyboardShortcuts;
